/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, {
  IresponseData,
  IresponseDataPaginated,
  ImportResult,
  fetchExportBlob,
} from "@/lib/api";
import { Subscription } from "./subscription";

export enum EclientStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}
export enum EfromClient {
  WEB = "WEB",
  REFERRAL = "REFERRAL",
  ADVERTISEMENT = "ADVERTISEMENT",
  FACEBOOK = "FACEBOOK",
  INSTAGRAM = "INSTAGRAM",
  OTHER = "OTHER",
}
export interface Client {
  id: string;
  name: string;
  phone: string;
  email: string;
  notes?: string;
  status: EclientStatus;
  clientFrom: EfromClient;
  activeServicesCount: number;
  monthlyValue: number;
  notify: boolean;
  createdAt: string;
  subscriptions: Subscription[];
}

// Tipos para las peticiones
export interface RegisterClientRequest {
  name: string;
  phone: string;
  email: string;
  notes?: string;
  from: EfromClient;
  notify?: boolean;
}

export interface UpdateClientRequest {
  name?: string;
  phone?: string;
  email?: string;
  notes?: string;
  status?: EclientStatus;
  from?: EfromClient;
  notify?: boolean;
}

// Export / Import
export interface ClientExportFilters {
  status?: EclientStatus;
  search?: string;
  notify?: boolean;
}

export interface ImportClientsRequest {
  file: string; // Excel en base64
  updated: boolean;
}

/**
 * Elimina campos vacíos de un objeto
 */
function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

/**
 * Servicio para gestionar clientes
 */
export const clientService = {
  /**
   * Registrar un nuevo cliente
   */
  async register(data: RegisterClientRequest): Promise<IresponseData<Client>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<Client>>(
        "/clients",
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el cliente. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener todos los clientes
   */
  async getAll(filter?: {
    page?: number;
    limit?: number;
    status?: EclientStatus;
    search?: string;
    notify?: boolean;
  }): Promise<IresponseDataPaginated<Client[]>> {
    try {
      const params = new URLSearchParams();
      if (filter?.page) params.append("page", filter.page.toString());
      if (filter?.limit) params.append("limit", filter.limit.toString());
      if (filter?.status) params.append("status", filter.status);
      if (filter?.search) params.append("search", filter.search);
      if (filter?.notify !== undefined) params.append("notify", filter.notify.toString());
      const response = await apiClient.get<IresponseDataPaginated<Client[]>>(
        "/clients",
        {
          params,
        },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los clientes. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener un cliente por ID
   */
  async getOne(id: string): Promise<IresponseData<Client>> {
    try {
      const response = await apiClient.get<IresponseData<Client>>(
        `/clients/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener el cliente. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Actualizar un cliente
   */
  async update(
    id: string,
    data: UpdateClientRequest,
  ): Promise<IresponseData<Client>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<Client>>(
        `/clients/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el cliente. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Exportar clientes a un archivo (Excel)
   */
  async exportClients(
    filters?: ClientExportFilters,
  ): Promise<{ blob: Blob; fileName: string }> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      return await fetchExportBlob(
        "/clients/export",
        cleanFilters,
        `clientes-export-${Date.now()}.xlsx`,
      );
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al exportar los clientes. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Importar clientes desde un archivo Excel (base64)
   */
  async importClients(
    data: ImportClientsRequest,
  ): Promise<IresponseData<ImportResult>> {
    try {
      const response = await apiClient.post<IresponseData<ImportResult>>(
        "/clients/import",
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al importar los clientes. Por favor, intenta de nuevo.",
      );
    }
  },
};
